import {
  DIFFICULTY_TAG_LABEL,
  normalizeBloomTag,
  normalizeDifficultyTag,
  suggestBloomLevel,
  suggestDifficultyTag,
  type SuggestedDifficulty,
} from "./suggest-tags"
import type {
  QuestionQualityAlternate,
  QuestionQualityCheck,
  QuestionQualitySnapshot,
} from "./types"

export const CLASSIFICATION_BLOOM_CHECK_ID = "classification-bloom"
export const CLASSIFICATION_DIFFICULTY_CHECK_ID = "classification-difficulty"

export type ClassificationAsk = {
  bloom: boolean
  difficulty: boolean
}

export function parseClassificationAsk(prompt: string): ClassificationAsk | null {
  const text = prompt.trim().toLowerCase()
  if (!text) return null
  const bloom = /bloom/.test(text)
  const difficulty = /difficult/.test(text)
  if (!bloom && !difficulty) {
    if (/suggest (the )?tags\b/.test(text) || /tag this (item|question)/.test(text)) {
      return { bloom: true, difficulty: true }
    }
    return null
  }
  return { bloom: bloom || !difficulty, difficulty: difficulty || !bloom }
}

function bloomAlternate(level: string): QuestionQualityAlternate {
  return {
    id: `classification-bloom-${level.toLowerCase()}`,
    text: level,
    actionKind: "set-bloom",
    actionLabel: "Use this Bloom’s",
    patch: { bloom: level },
    parameterDeltas: [],
    clearsCheckIds: [CLASSIFICATION_BLOOM_CHECK_ID],
  }
}

function difficultyAlternate(tag: SuggestedDifficulty): QuestionQualityAlternate {
  return {
    id: `classification-difficulty-${tag}`,
    text: DIFFICULTY_TAG_LABEL[tag],
    actionKind: "set-difficulty",
    actionLabel: "Use this difficulty",
    patch: { difficulty: tag },
    parameterDeltas: [],
    clearsCheckIds: [CLASSIFICATION_DIFFICULTY_CHECK_ID],
  }
}

export function classificationChecks(
  snapshot: QuestionQualitySnapshot,
  ask: ClassificationAsk,
  options?: { when?: "always" | "mismatch" },
): QuestionQualityCheck[] {
  const when = options?.when ?? "always"
  const checks: QuestionQualityCheck[] = []
  if (ask.bloom) {
    const bloom = suggestBloomLevel(snapshot.leadIn)
    if (
      bloom &&
      (when === "always" || normalizeBloomTag(snapshot.bloom) !== bloom)
    ) {
      const alternate = bloomAlternate(bloom)
      checks.push({
        id: CLASSIFICATION_BLOOM_CHECK_ID,
        parameter: "cognitive",
        severity: "caution",
        field: "settings",
        settingsTarget: "bloom",
        title: "Bloom’s",
        currentText: snapshot.bloom || undefined,
        mechanism: `This stem reads as ${bloom}.`,
        actionKind: "set-bloom",
        actionLabel: "Use this Bloom’s",
        patch: alternate.patch,
        alternates: [alternate],
      })
    }
  }
  if (ask.difficulty) {
    const difficulty = suggestDifficultyTag(snapshot.leadIn, snapshot.type)
    if (
      difficulty &&
      (when === "always" ||
        normalizeDifficultyTag(snapshot.difficulty) !== difficulty)
    ) {
      const alternate = difficultyAlternate(difficulty)
      checks.push({
        id: CLASSIFICATION_DIFFICULTY_CHECK_ID,
        parameter: "difficulty",
        severity: "caution",
        field: "settings",
        settingsTarget: "difficulty",
        title: "Difficulty",
        currentText: (() => {
          const tag = normalizeDifficultyTag(snapshot.difficulty)
          return tag ? DIFFICULTY_TAG_LABEL[tag] : undefined
        })(),
        mechanism: `This stem reads as ${DIFFICULTY_TAG_LABEL[difficulty]}.`,
        actionKind: "set-difficulty",
        actionLabel: "Use this difficulty",
        patch: alternate.patch,
        alternates: [alternate],
      })
    }
  }
  return checks
}
